import {
  createContext,
  use,
  useEffect,
  useId,
  useState,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
} from "react";
import { NavLink, type NavLinkProps } from "react-router-dom";
import { AnimatePresence, MotionConfig, motion, type Transition } from "motion/react";
import { ListIcon, XIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Aceternity „Resizable Navbar” (ui.aceternity.com/components/resizable-navbar) a projekthez
 * igazítva: Phosphor ikonok, react-router linkek, shadcn tokenek és `Button`.
 *
 * A sáv az oldal tetején áll, és görgetéskor a tartalommal együtt elgörög (nem ragadós). Az
 * eredeti görgetésre lebegő pillé zsugorodó állapota ezzel együtt kikerült: 100 px görgetés után
 * a sáv már nem is látszik, a zsugorodás a képernyőn kívül futna.
 *
 * Felépítés:
 *   <Navbar>
 *     <NavBody>          asztali sor (md-től): logó | <NavItems><NavItemLink/>…</NavItems> | műveletek
 *     <MobileNav>        mobil (md alatt)
 *       <MobileNavHeader> logó | <MobileNavToggle/>
 *       <MobileNavMenu>   lenyíló panel a fejléc alatt
 */

const spring: Transition = { type: "spring", stiffness: 200, damping: 50 };

/**
 * A `z-40` a mobil menü miatt kell: a fejléc alá nyíló panel a tartalom és a lebegő nézetváltó
 * (`FloatingBar`, `z-30`) fölött álljon.
 */
export function Navbar({ className, children, ...props }: ComponentProps<"header">) {
  return (
    <MotionConfig reducedMotion="user" transition={spring}>
      <header className={cn("relative z-40 w-full", className)} {...props}>
        {children}
      </header>
    </MotionConfig>
  );
}

interface NavBodyProps {
  className?: string;
  children: ReactNode;
}

/**
 * Asztali sor. A `min-w-fit` miatt sosem keskenyebb a tartalmánál: a logó, a linkek és a
 * műveletek nem csúsznak egymásra.
 */
export function NavBody({ className, children }: NavBodyProps) {
  return (
    <div
      className={cn(
        "container relative z-60 mx-auto hidden h-header w-full min-w-fit flex-row items-center justify-between gap-6 px-4 py-2 md:flex",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface NavItemsContextValue {
  /** A csúszó háttér `layoutId`-ja – példányonként egyedi, hogy két sáv ne mozgassa egymás pilljét */
  pillId: string;
  hovered: string | null;
  setHovered: (to: string | null) => void;
}

const NavItemsContext = createContext<NavItemsContextValue | null>(null);

/** A linksáv: egyetlen csúszó háttér követi az egérrel mutatott, nyugalomban az aktív linket */
export function NavItems({ className, children, ...props }: ComponentProps<"nav">) {
  const pillId = useId();
  const [hovered, setHovered] = useState<string | null>(null);
  return (
    <NavItemsContext value={{ pillId, hovered, setHovered }}>
      <nav
        onMouseLeave={() => setHovered(null)}
        className={cn(
          "flex min-w-0 flex-1 flex-row items-center justify-center gap-2 text-sm font-medium",
          className,
        )}
        {...props}
      >
        {children}
      </nav>
    </NavItemsContext>
  );
}

interface NavItemLinkProps extends Omit<NavLinkProps, "to" | "className" | "children"> {
  to: string;
  className?: string;
  /** Opcionális, mert `render` propként átadva (pl. `TooltipTrigger`) a Base UI teszi bele a gyerekeket */
  children?: ReactNode;
}

/** Egy link a `NavItems`-ben (react-router `NavLink`, `aria-current` az aktívon) */
export function NavItemLink({ to, className, children, onMouseEnter, ...props }: NavItemLinkProps) {
  const context = use(NavItemsContext);
  if (!context) throw new Error("A NavItemLink csak NavItems-en belül használható");
  const { pillId, hovered, setHovered } = context;

  const handleMouseEnter = (event: MouseEvent<HTMLAnchorElement>) => {
    onMouseEnter?.(event);
    setHovered(to);
  };

  return (
    <NavLink
      to={to}
      onMouseEnter={handleMouseEnter}
      className={({ isActive }) =>
        cn(
          "relative rounded-full px-4 py-2 whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50",
          isActive && "text-foreground",
          className,
        )
      }
      {...props}
    >
      {({ isActive }) => (
        <>
          {(hovered === to || (hovered === null && isActive)) && (
            <motion.div
              layoutId={pillId}
              className="absolute inset-0 rounded-full bg-muted"
              aria-hidden="true"
            />
          )}
          <span className="relative z-20 flex items-center gap-2">{children}</span>
        </>
      )}
    </NavLink>
  );
}

interface MobileNavProps {
  className?: string;
  children: ReactNode;
}

export function MobileNav({ className, children }: MobileNavProps) {
  return (
    <div
      className={cn(
        "relative z-50 mx-auto flex h-header w-full max-w-[calc(100vw-2rem)] flex-col items-center justify-between py-2 md:hidden",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function MobileNavHeader({ className, children }: ComponentProps<"div">) {
  return (
    <div className={cn("flex w-full flex-row items-center justify-between", className)}>
      {children}
    </div>
  );
}

interface MobileNavMenuProps {
  /** A `MobileNavToggle` `aria-controls`-ához */
  id?: string;
  className?: string;
  children: ReactNode;
  isOpen: boolean;
  /** Escape-re hívódik; a kívülre kattintást és a navigálást a hívó kezeli */
  onClose: () => void;
}

/** A fejléc alá nyíló panel. Hosszú listánál a panel görgethető, a fejléc a helyén marad. */
export function MobileNavMenu({ id, className, children, isOpen, onClose }: MobileNavMenuProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id={id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "absolute inset-x-0 top-full z-50 mt-2 flex max-h-[calc(100dvh-6rem)] w-full flex-col items-stretch gap-4 overflow-y-auto rounded-lg bg-popover p-4 text-popover-foreground shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]",
            className,
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface MobileNavToggleProps extends Omit<ComponentProps<typeof Button>, "onClick"> {
  isOpen: boolean;
  onClick: () => void;
}

export function MobileNavToggle({ isOpen, onClick, className, ...props }: MobileNavToggleProps) {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-expanded={isOpen}
      onClick={onClick}
      className={cn("touch-manipulation", className)}
      {...props}
    >
      {isOpen ? <XIcon aria-hidden="true" /> : <ListIcon aria-hidden="true" />}
      <span className="sr-only">{isOpen ? "Menü bezárása" : "Menü megnyitása"}</span>
    </Button>
  );
}
